/**
 * StrategySubmitErrorAlert — the ONE surface a create-strategy submit failure
 * speaks through (F-LIVE2-40, fix round 1 of fix wave 9).
 *
 * Why it is its own component: create mode has TWO launch surfaces — the
 * wizard's review step and the quick-create screen — and fix wave 9 gave the
 * message to the review step only. A refused submit from quick create rendered
 * NOTHING (review Important #2): the dialog just sat there after the primary
 * stopped spinning. Both surfaces now render this component, so the copy rules
 * below exist once and neither surface can drift from the other.
 *
 * The copy rules (review Minor #4):
 *  - `refused` = the server validated the request away and the orchestrator
 *    deleted the document it had just created → «Nothing was created.»
 *  - otherwise the document SURVIVES (a 5xx / network / timeout failure whose
 *    server-side effect is unknown) → the operator is told the strategy exists
 *    and what they can do with it, AND that this form is no longer the place:
 *    closing drops whatever is typed here (FW9-R5c), because create mode holds
 *    no `strategyId` and is bound to no document. Owner rule — edits that vanish
 *    are announced, never silent (ruling FW9-R5d). The refused branch says
 *    nothing of the sort: nothing was created, so there is nothing to edit.
 *  - the server's message is a sentence of its own: it gets a full stop only
 *    when it does not already end in one, so «…screens are off» never reads
 *    «…screens are off Nothing was created.»
 */
import React from 'react';
import { Alert } from '@mui/material';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';

/** A submit failure as the dialog holds it: the server's words + the certainty. */
export interface StrategySubmitError {
  message: string;
  /** The refusal was SYNCHRONOUS and certain — nothing was created. */
  refused: boolean;
}

/** Ends the server's message as a sentence without doubling its punctuation. */
const asSentence = (message: string): string => {
  const trimmed = message.trim();
  if (trimmed.length === 0) return '';
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

/** The exact words each branch shows — exported so tests read the rule, not a copy of it. */
export const strategySubmitErrorText = (error: StrategySubmitError): string => {
  const sentence = asSentence(error.message);
  const message = sentence.length > 0 ? `${sentence} ` : '';
  return error.refused
    ? `The generation was refused: ${message}Nothing was created.`
    : `Strategy created, but the AI generation request failed: ${message}`
      + 'You can add blocks manually or regenerate with AI later.'
      + ' Changes made here are not saved — the strategy exists; edit it on the canvas.';
};

export interface StrategySubmitErrorAlertProps {
  error?: StrategySubmitError | null;
}

/**
 * Renders nothing without an error, so both surfaces can mount it
 * unconditionally above their primary.
 */
export const StrategySubmitErrorAlert: React.FC<StrategySubmitErrorAlertProps> = ({ error = null }) => {
  if (!error) return null;
  return (
    <Alert
      severity="error"
      role="alert"
      data-testid={STRATEGY_LIST.wizardSubmitError}
    >
      {strategySubmitErrorText(error)}
    </Alert>
  );
};

StrategySubmitErrorAlert.displayName = 'StrategySubmitErrorAlert';
